import { useCallback, useEffect, useRef, useState, lazy, Suspense } from "react";
import { X, Footprints, MapPin, Clock } from "lucide-react";
import { getFootstepHistory } from "../footsteps/footstepsApi.js";
import { formatServerDateTimeIct } from "../components/serverTime.js";
import { DetailSkeleton, MediaSkeleton } from "../components/Skeleton.jsx";
// P159 fix: the FREE basemap the Footstep menu itself uses - MapLibre GL over
// CARTO Voyager raster tiles, no API key, flat 2D. The first cut of this dialog
// copied the Google route map from the War Room inline panel, which needed a key
// and renders an honest "blocked" state without one - so on this deployment it
// showed no map at all. Lazy, exactly like FootstepHistoryPage loads it, so
// maplibre-gl stays out of the main bundle.
const RouteMap = lazy(() => import("../components/RouteMap.jsx"));

/**
 * Same shape the War Room drawer prints elsewhere - a real ICT stamp, never a
 * browser-local time relabelled "ICT" (the trap P144-F documented). This file
 * used to carry its own copy that fell into that exact trap: it neither
 * normalized the designator-less backend timestamp to UTC nor pinned the
 * formatter to Asia/Bangkok, so a point captured 07:40 ICT printed as
 * "00:40 ICT" in this list while RouteMap's popup printed "07:40 ICT" for the
 * very same point. See components/serverTime.js.
 */
function formatDateTimeTh(value) {
  return formatServerDateTimeIct(value);
}

const DIALOG_TITLE_ID = "wx-wr-fs-dialog-title";

/**
 * P159: this person's Footstep, opened from the War Room drawer as a dialog.
 *
 * The drawer used to ALSO carry an inline "แสดง Footstep วันนี้" panel further
 * down. That panel is gone (owner: "ตัด footstep วันนี้ออกเพราะมีด้านบนอยู่แล้ว"):
 * it was today-only and squeezed into a ~400px column - the wrong shape for a
 * map you are trying to read a route off - so having both only made an operator
 * choose between two routes to the same data. This is the same real data the
 * Footstep menu screen shows (GET /tracking/workers/{id}/footsteps, the same
 * range + purpose contract, the same access-log row written on the backend),
 * given the width to be legible and a range picker so an operator can look
 * past today without leaving the person they are investigating.
 *
 * Deliberately a dialog and not a route: the operator is mid-investigation on
 * this worker in the War Room, and a top-level page change loses the board
 * position they came from.
 */

/** The range choices, as offsets in days. Computed against ICT. */
const RANGES = [
  { key: "today", label: "วันนี้", days: 0 },
  { key: "3d", label: "3 วันล่าสุด", days: 2 },
  { key: "7d", label: "7 วันล่าสุด", days: 6 },
];

const PURPOSE = "WarRoomFootstepReview";

/**
 * ICT day boundaries converted to UTC. Thailand is UTC+7 with no DST, so a
 * fixed offset is correct here and a local-time conversion would silently shift
 * the window on a machine set to another timezone.
 */
function ictRangeUtc(daysBack) {
  const ICT_OFFSET_MS = 7 * 60 * 60 * 1000;
  const nowIct = new Date(Date.now() + ICT_OFFSET_MS);
  const endIct = Date.UTC(nowIct.getUTCFullYear(), nowIct.getUTCMonth(), nowIct.getUTCDate(), 23, 59, 59, 999);
  const startIct = Date.UTC(nowIct.getUTCFullYear(), nowIct.getUTCMonth(), nowIct.getUTCDate() - daysBack, 0, 0, 0, 0);
  return {
    fromUtc: new Date(startIct - ICT_OFFSET_MS).toISOString(),
    toUtc: new Date(endIct - ICT_OFFSET_MS).toISOString(),
  };
}

export default function FootstepDialog({ open, personId, displayName, onClose }) {
  const dialogRef = useRef(null);
  const [rangeKey, setRangeKey] = useState("today");
  const [state, setState] = useState({ status: "idle", data: null, error: null });
  // P159 fix: RouteMap already zooms (flyTo, zoom>=16) and shows the point's
  // date/time popup - but only when it is TOLD which point is active. The first
  // cut rendered it without activePointId/onSelectPoint, so clicking a dot did
  // nothing. The timeline below selects the same way, so list and map stay in
  // step in both directions, exactly as the Footstep menu page behaves.
  const [activePointId, setActivePointId] = useState(null);

  const load = useCallback(
    (key) => {
      if (!personId) {
        return;
      }
      const range = RANGES.find((item) => item.key === key) ?? RANGES[0];
      const { fromUtc, toUtc } = ictRangeUtc(range.days);
      setState({ status: "loading", data: null, error: null });
      getFootstepHistory(personId, { fromUtc, toUtc, purpose: PURPOSE })
        .then((data) => setState({ status: "ready", data, error: null }))
        .catch((error) => setState({ status: "error", data: null, error }));
    },
    [personId],
  );

  useEffect(() => {
    if (open) {
      setRangeKey("today");
      setActivePointId(null);
      load("today");
    }
  }, [open, load]);

  // Capture phase + stopImmediatePropagation: the War Room drawer underneath
  // has its own Escape handler, and one press must close only this layer.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    function handleKeyDown(event) {
      if (event.key !== "Escape") {
        return;
      }
      event.stopImmediatePropagation();
      onClose?.();
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const points = state.data?.points ?? [];

  return (
    <div
      className="wx-wr-fs-root"
      onMouseDown={(event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) {
          onClose?.();
        }
      }}
    >
      <div className="wx-wr-fs-backdrop" aria-hidden="true" />
      <div ref={dialogRef} className="wx-wr-fs-dialog" role="dialog" aria-modal="true" aria-labelledby={DIALOG_TITLE_ID}>
        <header className="wx-wr-fs-head">
          <div className="wx-wr-fs-head-id">
            <span className="wx-wr-fs-kicker">
              <Footprints size={16} aria-hidden="true" />
              FOOTSTEP
            </span>
            <h2 id={DIALOG_TITLE_ID} className="wx-wr-fs-title">
              {displayName ?? "-"}
            </h2>
          </div>
          <div className="wx-wr-fs-head-actions">
            <div className="wx-wr-fs-ranges" role="group" aria-label="ช่วงเวลา">
              {RANGES.map((range) => (
                <button
                  key={range.key}
                  type="button"
                  className={`wx-wr-fs-range${rangeKey === range.key ? " wx-wr-fs-range--active" : ""}`}
                  aria-pressed={rangeKey === range.key}
                  onClick={() => {
                    setRangeKey(range.key);
                    setActivePointId(null);
                    load(range.key);
                  }}
                >
                  {range.label}
                </button>
              ))}
            </div>
            <button type="button" className="wx-wr-fs-close" onClick={() => onClose?.()} aria-label="ปิดหน้าต่าง Footstep">
              <X size={17} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="wx-wr-fs-body">
          {state.status === "loading" || state.status === "idle" ? (
            <DetailSkeleton label="กำลังโหลด Footstep..." />
          ) : state.status === "error" ? (
            <div className="wx-wr-drawer-state" role="alert">
              <p className="wx-state-body">ไม่สามารถโหลด Footstep ได้ กรุณาลองใหม่อีกครั้ง</p>
              <button type="button" className="wx-button wx-button--ghost" onClick={() => load(rangeKey)}>
                ลองใหม่
              </button>
            </div>
          ) : points.length === 0 ? (
            // An honest empty state: a worker who did not move (or whose phone
            // was off) genuinely has no points, and that is information.
            <div className="wx-wr-drawer-state">
              <p className="wx-state-body">ไม่มี Footstep ในช่วงเวลาที่เลือก</p>
            </div>
          ) : (
            <div className="wx-wr-fs-content">
              <div className="wx-wr-fs-map">
                <Suspense fallback={<MediaSkeleton label="กำลังโหลดแผนที่..." height={620} />}>
                  {/* mode="route": the 2D route line over the flat basemap. */}
                  <RouteMap
                    points={points}
                    mode="route"
                    height={620}
                    activePointId={activePointId}
                    onSelectPoint={setActivePointId}
                  />
                </Suspense>
              </div>
              <div className="wx-wr-fs-side">
                <p className="wx-wr-fs-count">
                  <MapPin size={14} aria-hidden="true" />
                  {points.length.toLocaleString("th-TH")} จุด
                </p>
                <ol className="wx-wr-fs-timeline">
                  {points.map((point, index) => (
                    <li key={point.locationEventId ?? index}>
                      <button
                        type="button"
                        className={`wx-wr-fs-point${point.locationEventId === activePointId ? " wx-wr-fs-point--active" : ""}`}
                        aria-pressed={point.locationEventId === activePointId}
                        onClick={() => setActivePointId(point.locationEventId ?? null)}
                      >
                        <span className="wx-wr-fs-time">
                          <Clock size={12} aria-hidden="true" />
                          {formatDateTimeTh(point.capturedAtUtc)}
                        </span>
                        <span className="wx-wr-fs-coord">
                          {point.latitude?.toFixed?.(6)}, {point.longitude?.toFixed?.(6)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
